
// levelUtils.js

// Pondération centrale du système d'activité / XP.
// Tu pourras ajouter de nouvelles sources ici plus tard sans refaire tout le système.
export const XP_WEIGHTS = {
  // Forum
  posts: 3,
  replies: 1,

  // Contributions MovieFrenzy
  movieRatings: 0.5,
  movieReviews: 4,

  // Réputation sociale
  likesReceived: 0.5,
  likesGiven: 0.15,

  // Profil cinéphile
  profileCompleted: 3,
  profilePreview: 5,
  favoriteMovies: 1.5,
  favoriteActors: 1.5,
  favoriteGenres: 1.5,

  // Interactions spéciales / saisonnières
  zodiacProfile: 1,

  // Bonus réclamables une seule fois via notification / période spéciale.
  // La valeur stockée dans Firestore est déjà le nombre réel de XP.
  seasonalBonusPoints: 1,

  // Bonus fidélité : 5 ans, 10 ans, etc.
  // La valeur stockée dans Firestore est déjà le nombre réel de XP.
  loyaltyBonusPoints: 1,

  // Anciennes clés conservées temporairement pour éviter de casser d’anciens profils.
  // À ne plus utiliser dans les nouveaux patchs.
  zodiacPeriodVisit: 0,
  zodiacMovieClick: 0,
};

const LEVELS = [
  { min: 7000, key: "divinite_pellicule" },
  { min: 4000, key: "legende_7e_art" },
  { min: 2000, key: "maitre_mise_en_scene" },
  { min: 1000, key: "realisateur_emerite" },
  { min: 500, key: "chef_operateur_illumineur" },
  { min: 200, key: "scenariste_prometteur" },
  { min: 100, key: "assistant_realisateur" },
  { min: 50, key: "apprenti_monteur" },
  { min: 10, key: "cadreur_novice" },
  { min: 0, key: "aspirant_figuring" },
];

export function normalizeNumber(value) {
  const number = Number(value);

  if (!Number.isFinite(number)) {
    return 0;
  }

  return Math.max(0, number);
}

export const XP_DEBUG_CONFIG = {
  enabled: false,

  // null = ne force pas tout le monde au même score.
  // Exemple temporaire : defaultScore: 7500,
  defaultScore: null,
  users: {
    "UORGlrEg0rMBDAgtLexs3iDrAAp1": 7500,
    "CSTURhKP8UYi8FKAUwP8nso3ec23": 500,
  },
};

function hasOwnKey(object, key) {
  return Object.prototype.hasOwnProperty.call(object, key);
}

export function getDebugActivityScoreForUser(userId, activityScore = 0) {
  const realScore = normalizeNumber(activityScore);

  if (!XP_DEBUG_CONFIG.enabled) {
    return realScore;
  }

  const safeUserId = typeof userId === "string" ? userId.trim() : "";

  if (
    safeUserId &&
    XP_DEBUG_CONFIG.users &&
    hasOwnKey(XP_DEBUG_CONFIG.users, safeUserId)
  ) {
    return normalizeNumber(XP_DEBUG_CONFIG.users[safeUserId]);
  }

  if (
    XP_DEBUG_CONFIG.defaultScore !== null &&
    XP_DEBUG_CONFIG.defaultScore !== undefined
  ) {
    return normalizeNumber(XP_DEBUG_CONFIG.defaultScore);
  }

  return realScore;
}

export function getDisplayActivityScore(activityScore = 0, userId = null) {
  return getDebugActivityScoreForUser(userId, activityScore);
}

export function calculateActivityScore(sources = {}) {
  // Compatibilité avec l'ancien système :
  // si on envoie directement un nombre, on le retourne comme score.
  if (typeof sources === "number") {
    return normalizeNumber(sources);
  }

  return Object.entries(sources).reduce((total, [sourceName, value]) => {
    const weight = XP_WEIGHTS[sourceName] ?? 0;
    return total + normalizeNumber(value) * weight;
  }, 0);
}

export function getUserActivityScore(userData = {}, fallbackMessageCount = 0) {
  // Priorité 1 : score déjà calculé/storé.
  const explicitScore = normalizeNumber(
    userData.activityScore ?? userData.xp?.total
  );

  if (explicitScore > 0) {
    return explicitScore;
  }

  // Priorité 2 : calcul depuis les sources XP.
  const xpSources = userData.xp?.sources || {};

  const sources = {
    ...xpSources,

    posts: userData.posts ?? userData.postCount ?? xpSources.posts,
    replies: userData.replies ?? userData.replyCount ?? xpSources.replies,

    movieReviews: userData.movieReviews ?? xpSources.movieReviews,
    likesReceived: userData.likesReceived ?? xpSources.likesReceived,

    zodiacProfile: userData.zodiacProfile ?? xpSources.zodiacProfile,

    seasonalBonusPoints:
      userData.seasonalBonusPoints ?? xpSources.seasonalBonusPoints,

    loyaltyBonusPoints:
      userData.loyaltyBonusPoints ?? xpSources.loyaltyBonusPoints,

    // Anciennes clés neutralisées / compatibilité
    zodiacPeriodVisit: userData.zodiacPeriodVisit ?? xpSources.zodiacPeriodVisit,
    zodiacMovieClick: userData.zodiacMovieClick ?? xpSources.zodiacMovieClick,
    holidayInteraction: userData.holidayInteraction ?? xpSources.holidayInteraction,
  };

  const computedScore = calculateActivityScore(sources);

  if (computedScore > 0) {
    return computedScore;
  }

  // Priorité 3 : fallback ancien système.
  return normalizeNumber(
    userData.messageCount ??
    userData.messagesCount ??
    fallbackMessageCount ??
    0
  );
}

export function getLevelTitle(activityScore = 0) {
  const score = normalizeNumber(activityScore);
  const level = LEVELS.find(({ min }) => score >= min);

  return level?.key || "aspirant_figuring";
}

export function getLevelNumber(activityScore = 0) {
  const score = normalizeNumber(activityScore);

  if (score >= 7000) return 10;
  if (score >= 4000) return 9;
  if (score >= 2000) return 8;
  if (score >= 1000) return 7;
  if (score >= 500) return 6;
  if (score >= 200) return 5;
  if (score >= 100) return 4;
  if (score >= 50) return 3;
  if (score >= 10) return 2;

  return 1;
}

export function getLevelClass(activityScore = 0) {
  return `level-${getLevelNumber(activityScore)}`;
}


export function getAvatarLevelClass(activityScore = 0) {
  const score = normalizeNumber(activityScore);

  if (score >= 7000) return "level-10";
  if (score >= 4000) return "level-8-9";
  if (score >= 2000) return "level-6-7";
  if (score >= 1000) return "level-5";
  if (score >= 500) return "level-4";
  if (score >= 200) return "level-3";
  if (score >= 100) return "level-2";
  if (score >= 50) return "level-1";

  return "level-0";
}

